import React, { useEffect, useState } from 'react';
import { toast, Loading } from '@cotera/client/app/components';
import { useWhoami } from '@cotera/client/app/stores/org';
import { useAuth } from '@workos-inc/authkit-react';
import { Button } from '../ui/button';
import { PROVIDER_ICONS } from './constants';
import { ConnectedStatusCard } from './credential-connection-cards';
import { ToolConnectionStatusBadge } from './tool-connection-status';
import { triggerSlackBotInstall } from './credential-strategy-views/slack-oauth-install-popout';
import { emitToolCredentialUpdated } from '@cotera/client/app/etc/emit-tool-credential-updated';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useSlackBotSettingsModel } from '@cotera/client/v0/settings/slack-bot-settings.model';
import { SlackBotChannelAgentsSection } from '@cotera/client/v0/settings/slack-bot-channel-agents-section';

const SLACK_PROVIDER = 'slack';

const SlackIcon =
  PROVIDER_ICONS[SLACK_PROVIDER as keyof typeof PROVIDER_ICONS] ?? (() => null);

export const SlackBotConnector: React.FC<{
  title?: string;
  description?: string;
  connectLabel?: string;
}> = ({
  title = 'Slack Bot',
  description = 'Connect Slack and install Coco for workspace conversations.',
  connectLabel = 'Install Coco',
}) => {
  const orgId = useWhoami((s) => s.org.id);
  const { user } = useAuth();
  const model = useSlackBotSettingsModel();
  const status = useWatchableValue(model.status);
  const config = useWatchableValue(model.config);
  const [isInstalling, setIsInstalling] = useState(false);
  const [hasError, setHasError] = useState(false);

  const isInstalled = config.installed;
  const isLoading = status === 'loading';

  useEffect(() => {
    if (!isInstalling) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsInstalling(false);
    }, 5 * 60 * 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isInstalling]);

  const handleInstall = (): void => {
    const userId = user?.id;
    if (userId === undefined || userId.length === 0) {
      toast.error('Unable to determine user ID for Slack authorization');
      return;
    }

    setIsInstalling(true);
    setHasError(false);

    try {
      triggerSlackBotInstall({ orgId, userId }, () => {
        emitToolCredentialUpdated(orgId, SLACK_PROVIDER);
        void model.refresh();
        setIsInstalling(false);
        toast.success('Slack bot installed successfully');
      });
    } catch {
      setIsInstalling(false);
      setHasError(true);
      toast.error('Failed to open Slack authorization popup');
    }
  };

  return (
    <div className="space-y-2">
      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <SlackIcon />
            </div>
            <div>
              <p className="text-sm font-medium">{title}</p>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          </div>

          {isLoading ? (
            <Loading.Shimmer className="h-8 w-24 rounded-md" />
          ) : isInstalled ? (
            <ToolConnectionStatusBadge label="Installed" />
          ) : (
            <Button size="sm" onClick={handleInstall} disabled={isInstalling}>
              {isInstalling ? (
                <>
                  <Loading.Spinner variant="sm" />
                  Installing...
                </>
              ) : (
                connectLabel
              )}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="mt-4 space-y-4">
            <Loading.Shimmer className="h-24 w-full rounded-md" />
            <Loading.Shimmer className="h-32 w-full rounded-md" />
          </div>
        ) : isInstalled ? (
          <div className="mt-4 space-y-4">
            <ConnectedStatusCard
              title="Coco is installed in Slack"
              description={
                config.teamId !== undefined
                  ? `Connected to workspace ${config.teamId}. Coco is available in DMs and @mentions.`
                  : 'Your workspace can use Coco from DMs and @mentions.'
              }
              variant="success"
              isActive={true}
            />
            <SlackBotChannelAgentsSection />
          </div>
        ) : null}
      </div>

      {hasError && (
        <p className="text-sm text-destructive">
          Could not update bot installation right now. Please try again.
        </p>
      )}
    </div>
  );
};
